import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { validateObjectDefinitionSpec, type ObjectDefinitionIssue } from 'shared-types';
import { useObjectDefinitionSaveDraftMutation } from '../../types/graphqlTypes';
import type { EditorStore } from './document/EditorStore';

export type SaveStatus = 'saved' | 'pending' | 'saving' | 'invalid' | 'error';

const DEBOUNCE_MS = 800;

/**
 * Saves the draft a moment after every change. An invalid spec is not sent
 * (the server would refuse it): the status says so and `issues` lists why.
 * `saveNow` flushes immediately (before publishing, on Ctrl+S) and reports
 * whether the draft on the server is now current.
 */
export function useAutosave({ store, organisationId, definitionId }: { store: EditorStore; organisationId: string; definitionId: string }) {
  const state = useSyncExternalStore(store.subscribe, store.getState);
  const [saveDraft] = useObjectDefinitionSaveDraftMutation();
  const [savedRevision, setSavedRevision] = useState(state.revision);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const inFlight = useRef<Promise<boolean> | null>(null);

  const issues: ObjectDefinitionIssue[] = useMemo(() => validateObjectDefinitionSpec(state.spec), [state.spec]);
  const dirty = state.revision !== savedRevision;

  const saveNow = useCallback(async (): Promise<boolean> => {
    if (inFlight.current) await inFlight.current;
    const { spec, revision } = store.getState();
    if (validateObjectDefinitionSpec(spec).length > 0) return false;
    const request = (async () => {
      setSaving(true);
      try {
        await saveDraft({ variables: { organisationId, definitionId, spec } });
        setSavedRevision(revision);
        setFailed(false);
        return true;
      } catch {
        setFailed(true);
        return false;
      } finally {
        setSaving(false);
      }
    })();
    inFlight.current = request;
    const ok = await request;
    inFlight.current = null;
    return ok && store.getState().revision === revision;
  }, [store, saveDraft, organisationId, definitionId]);

  useEffect(() => {
    if (!dirty || issues.length > 0) return;
    const timer = window.setTimeout(() => void saveNow(), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [dirty, issues.length, state.revision, saveNow]);

  // Leaving with unsaved edits: the browser asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** After a server-side reset (discard): the document now equals the server. */
  const markSaved = useCallback(() => setSavedRevision(store.getState().revision), [store]);

  const status: SaveStatus = saving ? 'saving' : issues.length > 0 && dirty ? 'invalid' : failed ? 'error' : dirty ? 'pending' : 'saved';
  return { status, issues, saveNow, markSaved, dirty };
}
